// The built-in schema for the unified "Document review" RMS view. Unlike
// every other RMS view kind, this schema is NOT served by the backend
// bootstrap or the /rms/schema/{kind} endpoint — it's a fixed, org-agnostic
// column layout the frontend owns. `useEntitySchema("document_review")`
// returns this constant directly so the grid can render its column picker /
// filter panel without a network round-trip.
//
// The column NAMES here are the contract with the backend: the document-review
// search endpoint (POST /rms/review/document-review/search) returns rows keyed
// by these same names, and filter/sort address columns by name. Keep them in
// lockstep with `_DOC_REVIEW_COLUMNS` in fp-backend/fp/routes/rms_review.py.
//
// Column-level provenance here is only a placeholder/fallback: the backend
// emits a per-row `RowCell.override.provenance` on every cell, and the grid
// resolves the EFFECTIVE editability/write-routing from that override (a
// document row's Note type is `derived`/read-only while an interaction row's
// is editable `interaction_field`). See cellReadOnly + persistence.

import type { Provenance, RmsSchema, SchemaFact } from "../Dashboard/types";

// Status enum values + the labels the grid's enum chip renders for each.
export const DOCUMENT_REVIEW_STATUS_VALUES = [
  "pending",
  "queued",
  "synced",
  "skipped",
] as const;

// Source enum values: the object's ingestion channel. Mirrors the backend
// `_SOURCE_VALUES` (and the `SourceKind` literal). Read-only, but typed as an enum
// so the column renders/filters as a fixed set instead of free text.
export const DOCUMENT_REVIEW_SOURCE_VALUES = [
  "Upload",
  "Email",
  "Notetaker",
  "Bipsync",
  "Shared drive",
] as const;

export const DOCUMENT_REVIEW_STATUS_LABELS: Record<string, string> = {
  pending: "Pending review",
  queued: "Reviewed, syncing",
  synced: "Reviewed, synced",
  skipped: "Reviewed, skipped",
};

// The Status column's judgement, in the five semantic states (#11426, round
// 5): awaiting review needs attention, syncing is in progress, synced is
// done, skipped is parked. The paint sites map these onto the token ramp —
// no hex is declared here because none may reach paint.
// Mirrors the backend _STATUS_COLORS so the rendered chip matches the endpoint.
export const DOCUMENT_REVIEW_STATUS_STATES: Record<string, string> = {
  pending: "watch",
  queued: "eval",
  synced: "active",
  skipped: "closed",
};

// The "New entities?" column is a plain boolean (true == the row links >=1 newly-created
// manager/fund/company). These colors are NOT the column's -- the column renders
// a sparkle/dash -- they decorate the per-entity New/Matched markers in the
// row-expand panel (DocReviewExpandPanel), kept here so that panel and the chip
// treatment share one palette.
export const DOCUMENT_REVIEW_NEW_COLORS: Record<string, string> = {
  new: "#7C3AED", // purple — newly created during processing
  matched: "#64748B", // slate — matched to a pre-existing entity
};

// Fill the verbose SchemaFact shape with the defaults a built-in,
// non-AI-managed column uses, so the per-column definitions below stay
// readable and only spell out what differs.
function fact(
  partial: Pick<SchemaFact, "id" | "name" | "position" | "provenance"> &
    Partial<SchemaFact>
): SchemaFact {
  return {
    inverse_name: null,
    is_system_managed: true,
    is_required: false,
    is_array: false,
    is_range: false,
    is_enum: false,
    is_hierarchical: false,
    ai_autofill: false,
    renew_after: null,
    ai_cadence: null,
    ai_cadence_day: null,
    scalar_type: null,
    scalar_unit: null,
    scalar_values: null,
    enum_colors: null,
    enum_labels: null,
    entity_types: null,
    description: partial.name,
    default_scalar_value: null,
    default_entity_ids: null,
    group_id: null,
    is_sortable: true,
    // The review grid cannot group: its request carries no group_by and the
    // endpoint has none, so no column offers it (#13416).
    is_groupable: false,
    // The review queue is a process surface: its entity columns keep the
    // CHIP grammar (rule 2's declared exception, #11426 gaps-v6 item 11)
    // because the chips carry review affordances — the purple is_new
    // marker, the affiliation tint, the per-link reasoning hover.
    ...(partial.entity_types && partial.entity_types.length > 0
      ? { entity_chips: true }
      : {}),
    ...partial,
  };
}

const BIPSYNC_TITLE_PROV: Provenance = {
  kind: "readonly",
  reason: "per-row bipsync title",
};
const AUTHOR_PROV: Provenance = {
  kind: "readonly",
  reason: "document uploader / interaction author",
};
const SOURCE_PROV: Provenance = {
  kind: "readonly",
  reason: "object source",
};
const NOTE_TYPE_PROV: Provenance = {
  kind: "readonly",
  reason: "per-row note type",
};
const UPLOADED_AT_PROV: Provenance = {
  kind: "readonly",
  reason: "blob upload timestamp",
};
const SUBTYPE_PROV: Provenance = {
  kind: "readonly",
  reason: "per-row subtype",
};
const EFFECTIVE_DATE_PROV: Provenance = {
  kind: "readonly",
  reason: "per-row effective date",
};
const STATUS_PROV: Provenance = {
  kind: "derived",
  reason: "derived sync status",
};
const NEW_PROV: Provenance = {
  kind: "derived",
  reason: "links a newly-created (vs matched) entity",
};

// The fact columns: Bipsync Title | Author | Source | Note type |
// Subtype | Funds | Managers | Companies | People | Effective date | Uploaded at |
// Status (Name is the leading name column, not a fact). Author, Source and
// Uploaded at are read-only identity columns;
// Funds/Managers/Companies/People are linked-entity arrays (Companies is read/edit
// only -- never created in Bipsync); Status is a derived enum. The research file
// name leads so the name a doc will carry in Bipsync sits next to the doc's own
// name in the Name column (#11311). Subtype's enum options
// (the org's document_type vocabulary) are org-specific, so they're injected into
// this fact at render time in SearchTabContent (mirroring the tags enum_colors
// injection) rather than hardcoded here.
export const DOCUMENT_REVIEW_SCHEMA: RmsSchema = {
  // No "Name" fact: Name is the grid's leading name column (rendered from each
  // row's top-level name), renamable via the standard rename path. (#10297)
  facts: [
    // The name this doc's Bipsync research file gets, from the org's naming
    // convention (#11311). Edited in place: a typed value is kept verbatim and
    // clearing the cell re-derives it from the template, mirroring the Name cell
    // (#10297). The provenance here is a placeholder -- the column lives on the
    // organization, so only the backend can name its id, and it supplies the real
    // `document_cell` provenance per row (readonly on interactions, which have no
    // research file of their own). Same arrangement as Subtype / Effective date.
    fact({
      id: "Bipsync Title",
      name: "Bipsync Title",
      position: 1,
      scalar_type: "string",
      provenance: BIPSYNC_TITLE_PROV,
    }),
    fact({
      id: "Author",
      name: "Author",
      position: 2,
      entity_types: ["person"],
      provenance: AUTHOR_PROV,
    }),
    fact({
      id: "Source",
      name: "Source",
      position: 3,
      scalar_type: "string",
      is_enum: true,
      scalar_values: [...DOCUMENT_REVIEW_SOURCE_VALUES],
      provenance: SOURCE_PROV,
    }),
    fact({
      id: "Note type",
      name: "Note type",
      position: 4,
      scalar_type: "string",
      provenance: NOTE_TYPE_PROV,
    }),
    fact({
      id: "Subtype",
      name: "Subtype",
      position: 5,
      scalar_type: "string",
      // Documents are multi-typed (#4012): the queue serves the cell as a
      // list and the write endpoint takes the full document_types list, so
      // the column edits through the multi-value editor (#11009).
      is_array: true,
      provenance: SUBTYPE_PROV,
    }),
    fact({
      id: "linked_entities:vehicle",
      name: "Funds",
      position: 6,
      is_array: true,
      entity_types: ["vehicle"],
      provenance: { kind: "linked_entities", entity_kind: "vehicle" },
    }),
    fact({
      id: "linked_entities:manager",
      name: "Managers",
      position: 7,
      is_array: true,
      entity_types: ["manager"],
      provenance: { kind: "linked_entities", entity_kind: "manager" },
    }),
    fact({
      id: "linked_entities:company",
      name: "Companies",
      position: 8,
      is_array: true,
      entity_types: ["company"],
      provenance: { kind: "linked_entities", entity_kind: "company" },
    }),
    // People: the interaction's primary-participant persons (or a document's
    // linked persons). Read/edit person array, mirroring Companies. For
    // interactions the backend supplies a per-cell interaction_field override
    // routing writes to person_entity_ids.
    fact({
      id: "linked_entities:person",
      name: "People",
      position: 9,
      is_array: true,
      entity_types: ["person"],
      provenance: { kind: "linked_entities", entity_kind: "person" },
    }),
    fact({
      id: "Effective date",
      name: "Effective date",
      position: 10,
      scalar_type: "date",
      provenance: EFFECTIVE_DATE_PROV,
    }),
    fact({
      id: "Uploaded at",
      name: "Uploaded at",
      position: 11,
      scalar_type: "date",
      provenance: UPLOADED_AT_PROV,
    }),
    fact({
      id: "Status",
      name: "Status",
      position: 12,
      scalar_type: "string",
      is_enum: true,
      scalar_values: [...DOCUMENT_REVIEW_STATUS_VALUES],
      enum_labels: DOCUMENT_REVIEW_STATUS_LABELS,
      status_states: DOCUMENT_REVIEW_STATUS_STATES,
      provenance: STATUS_PROV,
    }),
    // Plain boolean (Birb's call): true iff the row links a newly-created
    // manager/fund/company/person. Not an enum -- the grid renders a sparkle/dash
    // and the filter panel gives it a True/False toggle (SimpleClause), which the
    // doc-review search serializes to {column:"New entities?", value:"true"|"false"}.
    fact({
      id: "New entities?",
      name: "New entities?",
      position: 13,
      scalar_type: "boolean",
      provenance: NEW_PROV,
    }),
  ],
  groups: [],
};
